import { MouseEvent } from "react";
import { IntlShape } from "react-intl";
import Facebook from "@/icons/__generated__/Facebook/Facebook24";
import Instagram from "@/icons/__generated__/Instagram/Instagram24";
import LinkedIn from "@/icons/__generated__/LinkedIn/LinkedIn24";
import Pinterest from "@/icons/__generated__/Pinterest/Pinterest24";
import TikTok from "@/icons/__generated__/TikTok/TikTok24";
import Twitter from "@/icons/__generated__/Twitter/Twitter24";
import YouTube from "@/icons/__generated__/YouTube/YouTube24";
import {
  COMPONENT_NAMESPACES,
  FOOTER_NAMESPACES,
  useComponentMessages,
} from "../../../../intl-shared-component/IntlGlobalMessages";

const {
  ABOUT,
  ACTIVITIES,
  AMBASSADOR_PROGRAM,
  CALIFORNIA_PRIVACY_LINK,
  CAREERS,
  COMMUNITY,
  COMPANY,
  COUNTRIES,
  DOWNLOAD,
  EXPLORE,
  HELP_CENTER,
  HOST,
  LIST_YOUR_SPACE,
  LOCATIONS,
  PRESS,
  PRIVACY,
  RESOURCES,
  SUPPORT,
  TERMS,
  TRUST_AND_SAFETY,
  YOUR_PRIVACY_CHOICES,
} = FOOTER_NAMESPACES;

// Export Locale type
export type Locale = {
  country?: string;
  google_locale?: string;
  language?: string;
  region?: string;
  translations?: string;
  url_prefix?: string;
  zendesk_url_locale?: string;
  fallback?: boolean;
};

// Active locales
export const activeLocales: Locale[] = [
  {
    country: "United States",
    google_locale: "en",
    language: "English",
    region: "The Americas",
    translations: "en-US",
    url_prefix: "",
    zendesk_url_locale: "en-us",
  },
  {
    country: "Germany",
    google_locale: "de",
    language: "German",
    region: "Germany",
    translations: "de-DE",
    url_prefix: "de",
    zendesk_url_locale: "de-DE",
    fallback: false,
  },
  {
    country: "Deutschland",
    google_locale: "de",
    language: "Deutsch",
    region: "Europe",
    translations: "de-DE",
    url_prefix: "de",
    zendesk_url_locale: "en-us",
  },
  {
    country: "Australia",
    google_locale: "en-AU",
    language: "English",
    region: "Asia Pacific",
    translations: "en-GB",
    url_prefix: "au",
    zendesk_url_locale: "en-us",
  },
  {
    country: "Canada",
    google_locale: "en",
    language: "English",
    region: "The Americas",
    translations: "en-US",
    url_prefix: "ca",
    zendesk_url_locale: "en-us",
  },
  {
    country: "United Kingdom",
    google_locale: "en-GB",
    language: "English",
    region: "Europe",
    translations: "en-GB",
    url_prefix: "uk",
    zendesk_url_locale: "en-us",
  },
  {
    country: "España",
    google_locale: "es",
    language: "Español",
    region: "Europe",
    translations: "es-ES",
    url_prefix: "es",
    zendesk_url_locale: "en-us",
  },
  {
    country: "France",
    google_locale: "fr",
    language: "Français",
    region: "Europe",
    translations: "fr-FR",
    url_prefix: "fr",
    zendesk_url_locale: "fr",
  },
  {
    fallback: true,
    google_locale: "en",
    translations: "en-US",
    url_prefix: "",
  },
  {
    fallback: true,
    google_locale: "fr",
    translations: "fr-FR",
    url_prefix: "fr",
  },
];

// Privacy Data
export const privacyData = (formatComponentMessage: (id: string) => string) => {
  const privacyText = formatComponentMessage(YOUR_PRIVACY_CHOICES);

  return {
    privacyText,
    privacyOnClick: () => {
      // Define the actual privacy onClick behavior here
    },
  };
};

// Footer Navigation Links as a Static Array
export const footerNavLinks = (
  formatComponentMessage: (id: string) => string,
  urlPrefix: string = "",
  zendeskLocale: string = "",
  analyticsTrackFunction?: (e: MouseEvent<HTMLElement>, id: string) => void,
) => [
  {
    sectionTitle: formatComponentMessage(COMPANY),
    id: "header_link_company",
    links: [
      {
        text: formatComponentMessage(ABOUT),
        href: "https://book.peerspace.com/about",
        id: "footer_link_about",
        onClick: analyticsTrackFunction,
      },
      {
        text: formatComponentMessage(PRESS),
        href: "//try.peerspace.com/press/",
        id: "footer_link_press",
      },
      {
        text: formatComponentMessage(CAREERS),
        href: "https://jobs.lever.co/peerspace",
        id: "footer_link_careers",
        onClick: analyticsTrackFunction,
      },
    ],
  },
  {
    sectionTitle: formatComponentMessage(EXPLORE),
    id: "header_link_explore",
    links: [
      {
        text: formatComponentMessage(ACTIVITIES),
        href: `${urlPrefix ? `/${urlPrefix}` : ""}/plan/activities`,
      },
      {
        text: formatComponentMessage(LOCATIONS),
        href: `${urlPrefix ? `/${urlPrefix}` : ""}/venues/locations`,
      },
      {
        text: formatComponentMessage(RESOURCES),
        href: "/resources",
      },
      {
        text: formatComponentMessage(AMBASSADOR_PROGRAM),
        href: "https://madein.peerspace.com/ambassadors",
      },
    ],
  },
  {
    sectionTitle: formatComponentMessage(HOST),
    id: "header_link_host",
    onClick: analyticsTrackFunction,
    links: [
      {
        text: formatComponentMessage(LIST_YOUR_SPACE),
        href: `${urlPrefix ? `/${urlPrefix}` : ""}/host`,
        id: "footer_link_add_space",
        onClick: analyticsTrackFunction,
      },
      {
        text: formatComponentMessage(COMMUNITY),
        href: "https://community.peerspace.com",
      },
    ],
  },
  {
    sectionTitle: formatComponentMessage(COUNTRIES),
    id: "header_link_countries",
    links: activeLocales
      .filter((locale) => !locale.fallback)
      .map((locale) => ({
        text: locale.country,
        href: `/${locale.url_prefix}`,
      })),
  },
  {
    sectionTitle: formatComponentMessage(SUPPORT),
    id: "header_link_support",
    onClick: analyticsTrackFunction,
    links: [
      {
        text: formatComponentMessage(HELP_CENTER),
        href: `https://support.peerspace.com/hc/${zendeskLocale}`,
        id: "footer_link_support",
        onClick: analyticsTrackFunction,
      },
      {
        text: formatComponentMessage(TRUST_AND_SAFETY),
        href: "https://book.peerspace.com/trust-and-safety",
      },
      {
        text: formatComponentMessage(TERMS),
        href: `${urlPrefix ? `/${urlPrefix}` : ""}/legal/terms`,
        id: "footer_link_terms",
        onClick: analyticsTrackFunction,
      },
      {
        text: formatComponentMessage(PRIVACY),
        href: `${urlPrefix ? `/${urlPrefix}` : ""}/legal/privacy`,
        id: "footer_link_privacy",
        onClick: analyticsTrackFunction,
      },
      {
        text: formatComponentMessage(CALIFORNIA_PRIVACY_LINK),
        href: `${urlPrefix ? `/${urlPrefix}` : ""}/legal/privacy#california-privacy`,
      },
    ],
  },
  {
    sectionTitle: formatComponentMessage(DOWNLOAD),
    id: "header_link_download",
    onClick: analyticsTrackFunction,
    links: [
      {
        text: "App Store",
        href: "https://apps.apple.com/us/app/peerspace-book-unique-venues/id823879288?ls=1&_branch_match_id=976495954150499816&_branch_referrer=H4sIAAAAAAAAA8soKSkottLXL0hNLSouSExO1UssKNDLyczL1i9P9A8ITUz0MgwGAHjsqXolAAAA",
        id: "footer_link_mobile",
        onClick: analyticsTrackFunction,
      },
      {
        text: "Google Play Store",
        href: "https://play.google.com/store/apps/details?id=com.peerspace.app",
        id: "footer_link_mobile",
        onClick: analyticsTrackFunction,
      },
    ],
  },
];

export const useFooterMessages = ({
  intl,
  urlPrefix,
  zendeskLocale,
}: {
  intl: IntlShape;
  urlPrefix: string;
  zendeskLocale: string;
}) => {
  const { formatComponentMessage } = useComponentMessages(
    COMPONENT_NAMESPACES.FOOTER,
    intl,
  );
  return {
    footerNavLinks: footerNavLinks(
      formatComponentMessage,
      urlPrefix,
      zendeskLocale,
    ),
    privacyData: privacyData(formatComponentMessage),
  };
};

// Footer Social Links
export const footerSocialLinks = (
  analyticsTrackFunction?: (e: MouseEvent<HTMLElement>, id: string) => void,
) => [
  {
    title: "YouTube",
    icon: <YouTube />,
    href: "https://www.youtube.com/c/Peerspace",
    id: "footer_link_youtube",
  },
  {
    title: "TikTok",
    icon: <TikTok />,
    href: "https://www.tiktok.com/discover/Peerspace",
    id: "footer_link_tiktok",
  },
  {
    title: "X",
    icon: <Twitter />,
    href: "http://www.x.com/peerspace/",
    id: "footer_link_twitter",
    onClick: analyticsTrackFunction,
  },
  {
    title: "Pinterest",
    icon: <Pinterest />,
    href: "http://www.pinterest.com/peerspace/",
    id: "footer_link_pinterest",
    onClick: analyticsTrackFunction,
  },
  {
    title: "LinkedIn",
    icon: <LinkedIn />,
    href: "https://www.linkedin.com/company/peerspace",
    id: "footer_link_linkedin",
    onClick: analyticsTrackFunction,
  },
  {
    title: "Instagram",
    icon: <Instagram />,
    href: "https://www.instagram.com/peerspace/",
    id: "footer_link_instagram",
  },
  {
    title: "Facebook",
    icon: <Facebook />,
    href: "https://www.facebook.com/peerspace/",
    id: "footer_link_facebook",
    onClick: analyticsTrackFunction,
  },
];
